import { type FormEvent, useEffect, useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import { Check, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocalization, type TranslationKey } from "../../lib/i18n";
import { APP_PROMPT_CONTEXT_KEY, AppDownloadPrompt } from "../home/AppDownloadPrompt";
import { StoreBadges } from "../home/StoreBadges";

type LandingChapter = {
  id: "market" | "picks" | "social" | "standings" | "track";
  labelKey: TranslationKey;
  contextKey: TranslationKey;
  headlineKey: TranslationKey;
  bodyKey: TranslationKey;
  imageSrc: string;
  imageAltKey: TranslationKey;
  reverse?: boolean;
};

type UnauthenticatedLandingPageProps = {
  onSignUp?: () => void;
  onSignIn?: () => void;
};

const chapters: LandingChapter[] = [
  {
    id: "market",
    labelKey: "guest.landing.nav.market",
    contextKey: "market.guest.context",
    headlineKey: "market.guest.emphasis",
    bodyKey: "market.guest.body",
    imageSrc: "/img/landing/soccer-market.png",
    imageAltKey: "guest.landing.market.imageAlt",
  },
  {
    id: "picks",
    labelKey: "guest.landing.nav.picks",
    contextKey: "picks.guest.context",
    headlineKey: "picks.guest.emphasis",
    bodyKey: "picks.guest.body",
    imageSrc: "/img/landing/soccer-picks.png",
    imageAltKey: "guest.landing.picks.imageAlt",
    reverse: true,
  },
  {
    id: "social",
    labelKey: "guest.landing.nav.social",
    contextKey: "guest.landing.social.context",
    headlineKey: "guest.landing.social.emphasis",
    bodyKey: "guest.landing.social.body",
    imageSrc: "/img/landing/soccer-social-chat.png",
    imageAltKey: "guest.landing.social.imageAlt",
  },
  {
    id: "standings",
    labelKey: "guest.landing.nav.standings",
    contextKey: "guest.landing.standings.context",
    headlineKey: "guest.landing.standings.emphasis",
    bodyKey: "guest.landing.standings.body",
    imageSrc: "/img/landing/soccer-standings.png",
    imageAltKey: "guest.landing.standings.imageAlt",
    reverse: true,
  },
  {
    id: "track",
    labelKey: "guest.landing.nav.track",
    contextKey: "guest.landing.track.context",
    headlineKey: "guest.landing.track.emphasis",
    bodyKey: "guest.landing.track.body",
    imageSrc: "/img/landing/soccer-portfolio-track.png",
    imageAltKey: "guest.landing.track.imageAlt",
  },
];

const chapterIds = chapters.map((chapter) => chapter.id);

export function UnauthenticatedLandingPage({ onSignUp, onSignIn }: UnauthenticatedLandingPageProps) {
  const clerk = useClerk();
  const { t } = useLocalization();
  const [isJoinFormOpen, setIsJoinFormOpen] = useState(false);
  const [isTrackJoinFormOpen, setIsTrackJoinFormOpen] = useState(false);
  const [isFooterJoinFormOpen, setIsFooterJoinFormOpen] = useState(false);
  const [poolCode, setPoolCode] = useState("");
  const [poolCodeError, setPoolCodeError] = useState<string | null>(null);
  const [activeChapter, setActiveChapter] = useState<string | null>(null);
  const [visibleChapterIds, setVisibleChapterIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const ratios = new Map<string, number>();
    const thresholds = Array.from({ length: 11 }, (_, i) => i / 10);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target.id, entry.intersectionRatio);
        }
        let bestId: string | null = null;
        let bestRatio = 0;
        for (const [id, ratio] of ratios) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }
        setActiveChapter(bestRatio > 0 ? bestId : null);
      },
      { threshold: thresholds },
    );
    for (const chapter of chapters) {
      const el = document.getElementById(chapter.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setVisibleChapterIds(new Set(chapterIds));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleChapterIds((current) => {
          let next: Set<string> | null = null;
          for (const entry of entries) {
            if (!entry.isIntersecting || current.has(entry.target.id)) continue;
            next ??= new Set(current);
            next.add(entry.target.id);
          }
          return next ?? current;
        });
      },
      { rootMargin: "0px 0px -20% 0px", threshold: 0.28 },
    );

    for (const chapter of chapters) {
      const el = document.getElementById(chapter.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  const handleSignUp = () => {
    if (clerk.openSignUp) {
      void clerk.openSignUp();
      return;
    }
    onSignUp?.();
  };

  const handleSignIn = () => {
    if (clerk.openSignIn) {
      void clerk.openSignIn();
      return;
    }
    onSignIn?.();
  };

  const handleCreatePoolSignUp = () => {
    window.sessionStorage.setItem("pendingCreatePoolIntent", "1");
    handleSignUp();
  };

  const handleJoinSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedCode = poolCode.trim();
    if (!trimmedCode) {
      setPoolCodeError(t("guest.landing.join.error.required"));
      return;
    }

    window.sessionStorage.setItem(APP_PROMPT_CONTEXT_KEY, "pool_code");
    window.sessionStorage.setItem("pendingPoolCode", trimmedCode);
    window.sessionStorage.setItem("pendingPoolCodeAuthSource", "landing_form");
    setPoolCodeError(null);
    handleSignUp();
  };

  return (
    <section className="min-h-screen bg-black text-white">
      {/* Sticky chapter nav — appears once user scrolls into story sections */}
      <nav
        aria-label={t("common.navigation")}
        className={`fixed right-6 top-1/2 z-50 hidden -translate-y-1/2 flex-col gap-4 transition-opacity duration-500 xl:flex ${
          activeChapter ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        {chapters.map((chapter) => (
          <a
            key={chapter.id}
            href={`#${chapter.id}`}
            aria-label={t(chapter.labelKey)}
            className="group flex items-center justify-end gap-2.5"
          >
            <span
              className={`font-display text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${
                activeChapter === chapter.id
                  ? "text-[#F5A623]"
                  : "text-white/30 group-hover:text-white/60"
              }`}
            >
              {t(chapter.labelKey)}
            </span>
            <span
              className={`h-1.5 rounded-full transition-all duration-300 ${
                activeChapter === chapter.id
                  ? "w-4 bg-[#F5A623]"
                  : "w-1.5 bg-white/28 group-hover:bg-white/60"
              }`}
            />
          </a>
        ))}
      </nav>

      <div className="relative min-h-screen overflow-hidden">
        <img
          src="/img/soccer-splash2.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-[1.02] object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.97)_0%,rgba(0,0,0,0.76)_42%,rgba(0,0,0,0.22)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(0,0,0,0.9)_0%,rgba(0,0,0,0.16)_45%,rgba(0,0,0,0.34)_100%)]" />

        <nav
          className="relative z-10 mx-auto flex max-w-[72rem] items-center px-4 pt-5 md:px-8"
          aria-label={t("common.navigation")}
        >
          <div className="inline-flex items-center rounded-lg border border-white/10 bg-black/40 px-3 py-2 shadow-[0_18px_44px_rgba(0,0,0,0.22)] backdrop-blur">
            <img
              src="/img/sportex100-square.png"
              alt="SportEx100"
              className="h-16 w-auto object-contain md:h-20"
            />
          </div>
        </nav>

        <div
          id="create"
          className="relative z-10 mx-auto grid min-h-[calc(100svh-7rem)] max-w-[72rem] grid-cols-1 items-center gap-8 px-4 pb-8 pt-10 md:grid-cols-[minmax(0,1fr)_minmax(240px,320px)] md:px-8 md:pb-10 md:pt-8 lg:gap-14 lg:pt-12"
        >
          <div className="max-w-[560px] pb-2">
            <p className="font-display text-sm font-bold uppercase tracking-[0.18em] text-white/82 md:text-base">
              {t("guest.landing.hero.context")}
            </p>
            <h1 className="mt-3 max-w-[9ch] font-display text-[58px] font-bold uppercase leading-[1.02] text-[#F5A623] md:text-[92px]">
              {t("guest.landing.hero.headline")}
            </h1>
            <p className="mt-5 max-w-[520px] text-lg font-semibold leading-8 text-white/88 md:text-xl">
              {t("guest.landing.hero.body")}
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:max-w-[360px]">
              <AppDownloadPrompt />
              <button
                type="button"
                onClick={handleCreatePoolSignUp}
                className="min-h-12 rounded-lg bg-[#F5A623] px-8 py-[14px] text-center font-display text-sm font-bold uppercase text-black transition-opacity hover:opacity-90"
              >
                {t("guest.landing.cta.createPool")}
              </button>
              <button
                type="button"
                onClick={() => setIsJoinFormOpen((isOpen) => !isOpen)}
                className="min-h-11 rounded-lg border border-white/18 bg-black/70 px-4 text-center font-display text-xs font-bold uppercase text-white transition-colors hover:border-[#F5A623]/70 hover:text-[#F5A623]"
                aria-expanded={isJoinFormOpen}
                aria-controls="landing-join-pool-form"
              >
                {t("guest.landing.cta.joinWithCode")}
              </button>
              {isJoinFormOpen && (
                <form
                  id="landing-join-pool-form"
                  className="rounded-lg border border-white/15 bg-black/72 p-3 shadow-[0_18px_44px_rgba(0,0,0,0.28)] backdrop-blur"
                  onSubmit={handleJoinSubmit}
                >
                  <label
                    htmlFor="landing-pool-code"
                    className="font-display text-xs font-bold uppercase tracking-[0.12em] text-white/76"
                  >
                    {t("guest.landing.join.poolCodeLabel")}
                  </label>
                  <div className="mt-2 flex gap-2">
                    <input
                      id="landing-pool-code"
                      name="poolCode"
                      value={poolCode}
                      onChange={(event) => {
                        setPoolCode(event.target.value.toUpperCase());
                        if (poolCodeError) setPoolCodeError(null);
                      }}
                      className="min-h-11 min-w-0 flex-1 rounded-lg border border-white/14 bg-white px-3 text-sm font-bold uppercase text-black outline-none transition-colors placeholder:text-black/42 focus:border-[#F5A623]"
                      placeholder={t("guest.landing.join.poolCodePlaceholder")}
                      autoComplete="off"
                      inputMode="text"
                    />
                    <button
                      type="submit"
                      className="min-h-11 shrink-0 rounded-lg bg-white px-4 font-display text-xs font-bold uppercase text-black transition-opacity hover:opacity-90"
                    >
                      {t("guest.landing.join.continue")}
                    </button>
                  </div>
                  <p className="mt-2 text-xs font-medium leading-5 text-white/66">
                    {t("guest.landing.join.helper")}
                  </p>
                  {poolCodeError && (
                    <p className="mt-2 text-xs font-bold text-[#FF3B30]" role="alert">
                      {poolCodeError}
                    </p>
                  )}
                </form>
              )}
              <p className="text-sm text-white/50">
                {t("guest.landing.cta.hasAccount")}{" "}
                <button
                  type="button"
                  onClick={handleSignIn}
                  className="font-semibold text-white/72 underline-offset-2 hover:text-white hover:underline"
                >
                  {t("auth.signIn")}
                </button>
              </p>
              <div className="mt-1 border-t border-white/10 pt-4">
                <p className="mb-3 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-white/36">
                  {t("guest.landing.cta.orDownload")}
                </p>
                <StoreBadges />
              </div>
            </div>
          </div>
          <div className="mx-auto hidden w-full max-w-[260px] pb-4 md:block">
            <div className="aspect-[1206/2622] overflow-hidden rounded-[28px] border-2 border-white/18 bg-black shadow-[0_30px_68px_rgba(0,0,0,0.65)]">
              <img
                src="/img/landing/soccer-order-form-open.png"
                alt={t("guest.landing.orderForm.imageAlt")}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Scroll nudge */}
        <div
          className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 animate-bounce flex-col items-center gap-1.5 md:flex"
          aria-hidden="true"
        >
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-white/36">
            Scroll
          </span>
          <ChevronDown className="h-4 w-4 text-white/36" />
        </div>
      </div>

      {chapters.map((chapter) => {
        const isVisible = visibleChapterIds.has(chapter.id);

        return (
          <section
            key={chapter.id}
            id={chapter.id}
            className="bg-black"
          >
            <div
              className={`mx-auto grid min-h-screen max-w-[72rem] grid-cols-1 content-center items-center gap-10 px-4 py-14 md:grid-cols-2 md:px-8 md:py-20 xl:pr-24 2xl:pr-8 ${
                chapter.reverse ? "md:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div>
                <span
                  className={`inline-flex rounded-md border border-[#F5A623]/30 bg-[#F5A623]/10 px-3 py-2 font-display text-xs font-bold uppercase tracking-[0.08em] text-[#F5A623] transition-all duration-700 ease-out motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none ${
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
                  }`}
                >
                  {t(chapter.labelKey)}
                </span>
                <p
                  className={`mt-6 font-display text-sm font-bold uppercase tracking-[0.18em] text-white/72 transition-all delay-75 duration-700 ease-out motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none ${
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
                  }`}
                >
                  {t(chapter.contextKey)}
                </p>
                <h2
                  className={`mt-3 max-w-[10ch] font-display text-[48px] font-bold uppercase leading-[0.94] text-[#F5A623] transition-all delay-150 duration-700 ease-out motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none md:text-[72px] ${
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
                  }`}
                >
                  {t(chapter.headlineKey)}
                </h2>
                <p
                  className={`mt-5 max-w-[500px] text-lg font-medium leading-8 text-white/76 transition-all delay-200 duration-700 ease-out motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none ${
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
                  }`}
                >
                  {t(chapter.bodyKey)}
                </p>
                {chapter.id === "track" && (
                  <div
                    className={`mt-7 max-w-[420px] transition-all delay-300 duration-700 ease-out motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none ${
                      isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
                    }`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={handleCreatePoolSignUp}
                        className="min-h-12 rounded-lg bg-[#F5A623] px-6 py-3 text-center font-display text-sm font-bold uppercase text-black transition-opacity hover:opacity-90"
                      >
                        {t("guest.landing.cta.createPool")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsTrackJoinFormOpen((isOpen) => !isOpen)}
                        className="min-h-12 rounded-lg border border-white/18 bg-black/70 px-5 py-3 text-center font-display text-xs font-bold uppercase text-white transition-colors hover:border-[#F5A623]/70 hover:text-[#F5A623]"
                        aria-expanded={isTrackJoinFormOpen}
                        aria-controls="landing-track-join-pool-form"
                      >
                        {t("guest.landing.cta.joinWithCode")}
                      </button>
                    </div>

                    {isTrackJoinFormOpen && (
                      <form
                        id="landing-track-join-pool-form"
                        className="mt-3 rounded-lg border border-white/15 bg-black/72 p-3 shadow-[0_18px_44px_rgba(0,0,0,0.28)] backdrop-blur"
                        onSubmit={handleJoinSubmit}
                      >
                        <label
                          htmlFor="landing-track-pool-code"
                          className="font-display text-xs font-bold uppercase tracking-[0.12em] text-white/76"
                        >
                          {t("guest.landing.join.poolCodeLabel")}
                        </label>
                        <div className="mt-2 flex gap-2">
                          <input
                            id="landing-track-pool-code"
                            name="poolCode"
                            value={poolCode}
                            onChange={(event) => {
                              setPoolCode(event.target.value.toUpperCase());
                              if (poolCodeError) setPoolCodeError(null);
                            }}
                            className="min-h-11 min-w-0 flex-1 rounded-lg border border-white/14 bg-white px-3 text-sm font-bold uppercase text-black outline-none transition-colors placeholder:text-black/42 focus:border-[#F5A623]"
                            placeholder={t("guest.landing.join.poolCodePlaceholder")}
                            autoComplete="off"
                            inputMode="text"
                          />
                          <button
                            type="submit"
                            className="min-h-11 shrink-0 rounded-lg bg-white px-4 font-display text-xs font-bold uppercase text-black transition-opacity hover:opacity-90"
                          >
                            {t("guest.landing.join.continue")}
                          </button>
                        </div>
                        <p className="mt-2 text-xs font-medium leading-5 text-white/66">
                          {t("guest.landing.join.helper")}
                        </p>
                        {poolCodeError && (
                          <p className="mt-2 text-xs font-bold text-[#FF3B30]" role="alert">
                            {poolCodeError}
                          </p>
                        )}
                      </form>
                    )}

                    <a
                      href="#finale"
                      className="mt-4 inline-flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-white/46 transition-colors hover:text-[#F5A623]"
                    >
                      <span>{t("guest.landing.footer.kicker")}</span>
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    </a>
                  </div>
                )}
              </div>
              <div
                className={`mx-auto w-full max-w-[300px] transition-all delay-100 duration-700 ease-out motion-reduce:translate-y-0 motion-reduce:scale-100 motion-reduce:opacity-100 motion-reduce:transition-none ${
                  isVisible ? "translate-y-0 scale-100 opacity-100" : "translate-y-10 scale-[0.97] opacity-0"
                }`}
              >
                <div className="aspect-[1206/2622] overflow-hidden rounded-[30px] border-2 border-white/18 bg-black shadow-[0_30px_68px_rgba(0,0,0,0.65)]">
                  <img src={chapter.imageSrc} alt={t(chapter.imageAltKey)} className="h-full w-full object-cover" />
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section id="finale" className="relative overflow-hidden bg-black px-4 py-24 md:px-8 md:py-32">
        {/* Splash bookend — faint echo of the hero image */}
        <img
          src="/img/soccer-splash2.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.16]"
        />
        <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_0%,rgba(245,166,35,0.14)_0%,rgba(0,0,0,0)_55%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(0,0,0,0.92)_0%,rgba(0,0,0,0.7)_50%,rgba(0,0,0,0.85)_100%)]" />

        <div className="relative z-10 mx-auto flex max-w-[56rem] flex-col items-center text-center">
          <p className="font-display text-xs font-bold uppercase tracking-[0.22em] text-[#F5A623]">
            {t("guest.landing.footer.kicker")}
          </p>
          <h2 className="mt-5 max-w-[16ch] font-display text-[44px] font-bold uppercase leading-[0.98] text-white md:text-[76px]">
            {t("guest.landing.footer.title")}
          </h2>
          <p className="mt-6 max-w-[560px] text-lg font-medium leading-8 text-white/80 md:text-xl">
            {t("guest.landing.footer.body")}
          </p>

          <button
            type="button"
            onClick={handleCreatePoolSignUp}
            className="mt-10 min-h-14 rounded-xl bg-[#F5A623] px-12 py-4 font-display text-base font-bold uppercase tracking-wide text-black shadow-[0_18px_50px_rgba(245,166,35,0.35)] transition-transform duration-200 hover:scale-[1.03] hover:opacity-95"
          >
            {t("guest.landing.cta.createPool")}
          </button>

          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {[
              t("guest.landing.footer.chip.free"),
              t("guest.landing.footer.chip.noMoney"),
              t("guest.landing.footer.chip.anytime"),
            ].map((chip) => (
              <li key={chip} className="flex items-center gap-2 text-sm font-semibold text-white/70">
                <Check className="h-4 w-4 shrink-0 text-[#F5A623]" aria-hidden="true" />
                {chip}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex w-full max-w-[420px] flex-col items-stretch gap-3 rounded-2xl border border-white/10 bg-black/35 p-4 shadow-[0_18px_54px_rgba(0,0,0,0.28)] backdrop-blur">
            <div>
              <p className="mb-3 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                {t("guest.landing.cta.orDownload")}
              </p>
              <StoreBadges className="justify-center" />
            </div>

            <button
              type="button"
              onClick={() => setIsFooterJoinFormOpen((isOpen) => !isOpen)}
              className="min-h-11 rounded-lg border border-white/18 bg-black/70 px-4 text-center font-display text-xs font-bold uppercase text-white transition-colors hover:border-[#F5A623]/70 hover:text-[#F5A623]"
              aria-expanded={isFooterJoinFormOpen}
              aria-controls="landing-footer-join-pool-form"
            >
              {t("guest.landing.cta.joinWithCode")}
            </button>

            {isFooterJoinFormOpen && (
              <form
                id="landing-footer-join-pool-form"
                className="rounded-lg border border-white/15 bg-black/72 p-3 shadow-[0_18px_44px_rgba(0,0,0,0.28)] backdrop-blur"
                onSubmit={handleJoinSubmit}
              >
                <label
                  htmlFor="landing-footer-pool-code"
                  className="font-display text-xs font-bold uppercase tracking-[0.12em] text-white/76"
                >
                  {t("guest.landing.join.poolCodeLabel")}
                </label>
                <div className="mt-2 flex gap-2">
                  <input
                    id="landing-footer-pool-code"
                    name="poolCode"
                    value={poolCode}
                    onChange={(event) => {
                      setPoolCode(event.target.value.toUpperCase());
                      if (poolCodeError) setPoolCodeError(null);
                    }}
                    className="min-h-11 min-w-0 flex-1 rounded-lg border border-white/14 bg-white px-3 text-sm font-bold uppercase text-black outline-none transition-colors placeholder:text-black/42 focus:border-[#F5A623]"
                    placeholder={t("guest.landing.join.poolCodePlaceholder")}
                    autoComplete="off"
                    inputMode="text"
                  />
                  <button
                    type="submit"
                    className="min-h-11 shrink-0 rounded-lg bg-white px-4 font-display text-xs font-bold uppercase text-black transition-opacity hover:opacity-90"
                  >
                    {t("guest.landing.join.continue")}
                  </button>
                </div>
                <p className="mt-2 text-xs font-medium leading-5 text-white/66">
                  {t("guest.landing.join.helper")}
                </p>
                {poolCodeError && (
                  <p className="mt-2 text-xs font-bold text-[#FF3B30]" role="alert">
                    {poolCodeError}
                  </p>
                )}
              </form>
            )}

            <p className="text-sm text-white/50">
              {t("guest.landing.cta.hasAccount")}{" "}
              <button
                type="button"
                onClick={handleSignIn}
                className="font-semibold text-white/72 underline-offset-2 hover:text-white hover:underline"
              >
                {t("auth.signIn")}
              </button>
            </p>
          </div>

          <nav
            className="mt-12 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-white/10 px-2 pt-6 text-[13px] font-semibold text-white/48"
            aria-label="Legal and support links"
          >
            <Link
              className="transition-colors hover:text-[#F5A623] focus-visible:text-[#F5A623] focus-visible:outline-none"
              to="/privacy"
            >
              {t("settings.privacyPolicy")}
            </Link>
            <span className="text-white/20" aria-hidden="true">
              |
            </span>
            <Link
              className="transition-colors hover:text-[#F5A623] focus-visible:text-[#F5A623] focus-visible:outline-none"
              to="/terms"
            >
              {t("common.terms")}
            </Link>
            <span className="text-white/20" aria-hidden="true">
              |
            </span>
            <Link
              className="transition-colors hover:text-[#F5A623] focus-visible:text-[#F5A623] focus-visible:outline-none"
              to="/support"
            >
              {t("settings.support")}
            </Link>
          </nav>
        </div>
      </section>
    </section>
  );
}
